import React, { useRef, useState } from 'react';
import {
  useFloating,
  size,
  offset,
  autoPlacement,
  hide as hideMiddleware,
  autoUpdate,
  useInteractions,
  useClick,
  useListNavigation,
  useDismiss,
  useRole,
} from '@floating-ui/react';
import { AriaFocusProps } from '../../types/common';
import classNames from 'classnames';
import { IconDotsVertical, IconProps } from '../icons/generated';
import { Button } from './Button';
import type { ButtonColor, ButtonVariant, IconButtonColor } from './types';
import type { ButtonProps } from './Button';
import { DropdownOptions } from '../dropdown/DropdownOptions';
import { DEFAULT_DROPDOWN_MAX_HEIGHT } from '../dropdown/constants';
import { IconButton } from './IconButton';
import { DropdownOption } from '../dropdown/types';

export interface PopoverButtonProps<OptionValue extends string | number>
  extends Omit<ButtonProps, 'size' | 'value' | 'onChange' | 'variant' | 'color'> {
  /** @default 'button' */
  buttonType?: 'button' | 'icon-button';
  /** @default 'medium' */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /**
   * floating 요소가 reference 영역을 넘어갈 경우 자동 위치 처리 사용 유무
   *  - 자동 위치 상/하만 적용됩니다.
   * @default true
   */
  autoPlacement?: boolean;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  /** @default false */
  optionsMatchButtonWidth?: boolean;
  /**
   * Popover OptionList maxHeight값
   * @default DEFAULT_DROPDOWN_MAX_HEIGHT(400)
   */
  maxHeight?: number;
  disabled?: boolean;
  value?: OptionValue;
  /** @default 'outlined' */
  variant?: ButtonVariant;
  /** @default 'secondary' */
  color?: ButtonColor;
  icon?: React.ReactElement<IconProps, string | React.JSXElementConstructor<unknown>>;
  onChange?: (value: OptionValue) => void;
  options: Array<DropdownOption<OptionValue>>;
  /**
   * 커스텀 버튼 렌더링 함수
   */
  renderButton?: ({
    buttonProps,
  }: {
    buttonProps: { onClick: (e: React.MouseEvent) => void } & Record<string, unknown>;
  }) => React.ReactNode;
}

function isIconButtonColor(color: ButtonColor): color is IconButtonColor {
  return color === 'primary' || color === 'secondary' || color === 'destructive';
}

export const PopoverButton = <OptionValue extends string | number>({
  buttonType = 'button',
  size: sizeProp = 'medium',
  loading,
  type,
  variant = 'outlined',
  color = 'secondary',
  icon,
  leftIcon,
  className,
  children,
  zIndex,
  autoPlacement: autoPlacementProps = true,
  optionsMatchButtonWidth = false,
  options,
  value: valueProp,
  maxHeight = DEFAULT_DROPDOWN_MAX_HEIGHT,
  disabled,
  unstableFocusVisible,
  onChange,
  renderButton,
}: PopoverButtonProps<OptionValue> & AriaFocusProps) => {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const optionListRef = useRef<Array<HTMLElement | null>>([]);
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      size({
        apply({ rects, elements, availableWidth, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: optionsMatchButtonWidth ? `${rects.reference.width}px` : undefined,
            maxWidth: !optionsMatchButtonWidth ? `${availableWidth}px` : undefined,
            maxHeight: `${availableHeight < maxHeight ? availableHeight - 20 : maxHeight}px`,
          });
        },
      }),
      ...(autoPlacementProps
        ? [autoPlacement({ allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'], padding: 4 })]
        : []),
      hideMiddleware(),
    ],
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    useClick(context, { enabled: !disabled, event: 'mousedown' }),
    useDismiss(context),
    useRole(context, { role: 'listbox' }),
    useListNavigation(context, {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
    }),
  ]);

  const sharedButtonProps = {
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpen(false);
    },
    ...getReferenceProps({
      ref: refs.setReference,
      className: classNames('pds-dropdown-label', className),
      disabled,
      selected: selectedIndex !== -1,
      tabIndex: 0,
    }),
  };

  const buttonProps = {
    type,
    size: sizeProp,
    disabled,
    ...(buttonType === 'icon-button'
      ? {
          icon: icon || <IconDotsVertical />,
        }
      : {
          loading,
          leftIcon,
          rightIcon: <IconDotsVertical />,
          children,
        }),
    ...sharedButtonProps,
  };
  const supportsIconButtonStyle = variant === 'outlined' && isIconButtonColor(color);

  return (
    <>
      {typeof renderButton === 'function' ? (
        renderButton({ buttonProps: sharedButtonProps })
      ) : buttonType === 'icon-button' ? (
        <IconButton
          variant={supportsIconButtonStyle ? variant : undefined}
          color={supportsIconButtonStyle ? color : undefined}
          {...buttonProps}
        />
      ) : (
        <Button variant={variant} color={color} {...buttonProps} />
      )}
      <DropdownOptions
        refs={refs}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          setOpen(false);
          setFocusIndex(null);
        }}
        size={sizeProp}
        zIndex={zIndex}
        unstableFocusVisible={unstableFocusVisible}
        context={context}
        onChange={onChange}
        focusIndex={focusIndex}
        options={options}
      />
    </>
  );
};
